import { styled } from 'dripsy';
import React, { Fragment } from 'react';
import { ModalComponentProp } from 'react-native-modalfy';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import { createThunk } from 'mst-collection';

import { ModalStackParamsType } from '../../modals/types';
import { ModalView } from './components/ModalView';
import { BaseText } from '../common/BaseText';
import { CheckBox } from '../common/CheckBox';
import { Separator } from '../common/Separator';
import { Button, ButtonStyleEnum } from '../common/Button';
import { Loader } from '../common/Loader';
import { EmptyResultView } from '../views/EmptyResultView';
import { ButtonsLayout } from '../layouts/ButtonsLayout';
import { useAsyncInit } from '@hooks';
import { parseApiError } from '@utils';
import { ToastService } from '@services';
import { useSupportModel } from '@models/Support/SupportModel';

const ModalViewContainer = styled(ModalView)({
  p: '$8',
  width: 343,
  minWidth: 325,
});

const ModalButtonsLayout = styled(ButtonsLayout)({
  width: ['100%', 142],
});

const Title = styled(BaseText)({
  variant: 'text.xlarge',
});

const LoaderContainer = styled(View)({
  flex: [1, 0],
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 180,
  minWidth: 295,
});

const EmptyViewContainer = styled(LoaderContainer)({});

export interface IReportModalParams {
  item: {
    sendReport: ReturnType<typeof createThunk>;
  };
}

type ModalPropsType = ModalComponentProp<
  ModalStackParamsType,
  void,
  'ReportModal'
>;

export const ReportModal = observer(({ modal }: ModalPropsType) => {
  const { t } = useTranslation();
  const support = useSupportModel();

  useAsyncInit(async () => {
    try {
      await support.report.fetchReasonTypes.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  });

  function closeModal() {
    modal.closeModal();
  }

  async function send() {
    try {
      await modal.params?.item.sendReport.run(
        support.report.selectedReasonTypeIds,
      );

      ToastService.showSuccess(t('report.successMessage'));

      closeModal();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  const isLoading = support.report.fetchReasonTypes.inProgress;
  const isError = support.report.fetchReasonTypes.isError;

  return (
    <ModalViewContainer>
      <Title>{t('report.reasons')}</Title>
      <Separator />

      {isLoading && (
        <LoaderContainer>
          <Loader size="large" />
        </LoaderContainer>
      )}

      {!isLoading && isError && (
        <EmptyViewContainer>
          <EmptyResultView />
        </EmptyViewContainer>
      )}

      {!isLoading && !isError && (
        <View>
          {support.report.reasonTypes.map((item, index) => (
            <Fragment key={index}>
              {index !== 0 && <Separator size="$7" />}
              <CheckBox
                value={item.isSelected}
                text={item.title}
                onValueChange={item.toggleSelect}
              />
            </Fragment>
          ))}
        </View>
      )}

      <Separator />
      <ModalButtonsLayout>
        <Button
          disabled={!support.report.hasSelectedReasons}
          onPress={send}
          isLoading={modal.params?.item.sendReport.inProgress}
        >
          {t('report.report')}
        </Button>

        <Button
          onPress={closeModal}
          buttonStyle={ButtonStyleEnum.Outline}
        >
          {t('report.cancel')}
        </Button>
      </ModalButtonsLayout>
    </ModalViewContainer>
  );
});
